import { useMemo } from 'react';

/**
 * Custom hook to manage floorplan filtering logic
 */
export function useFloorplanFilters(floorplans, filters) {
    // Get unique home types
    const availableHomeTypes = useMemo(() => {
        const types = new Set();
        floorplans.forEach(plan => {
            const homeType = plan['Home Type'] || plan.HomeType || plan.homeType;
            if (homeType) {
                types.add(homeType);
            }
        });
        return Array.from(types).sort();
    }, [floorplans]);

    // Get unique bed counts
    const availableBeds = useMemo(() => {
        const beds = new Set();
        floorplans.forEach(plan => {
            const bedCount = plan.Beds || plan.beds;
            if (bedCount) {
                beds.add(bedCount);
            }
        });
        return Array.from(beds).sort((a, b) => a - b);
    }, [floorplans]);

    // Transform floorplan data to component format
    const transformedFloorplans = useMemo(() => {
        if (!floorplans || floorplans.length === 0) return [];

        return floorplans.map((plan, index) => {
            const id = plan.Id || plan.id;
            const name = plan.Name || plan.name;
            const beds = plan.Beds || plan.beds;
            const baths = plan.Baths || plan.baths;
            const garage = plan.Garage || plan.garage;
            const sqft = plan.SF || plan.sf;
            const stories = plan.Story || plan.story || plan.stories;
            const homeType = plan['Home Type'] || plan.HomeType || plan.homeType;
            const thumb = plan.Thumb || plan.thumb;

            return {
                id: id != null ? id : index,
                image: thumb || `/images/floorplans/${name?.toLowerCase().replace(/\s+/g, '-')}.png`,
                homeType: homeType || 'Standard',
                planName: name,
                beds: beds ? `${beds}+ Beds` : 'N/A',
                baths: baths ? `${baths}+ Baths` : 'N/A',
                garage: garage ? `${garage}+ Car Garage` : '2+ Car Garage',
                sqft: sqft ? `${sqft.toLocaleString()}+ SF` : 'N/A',
                stories: stories ? `${stories}+ Story` : '1+ Story',
                originalData: plan
            };
        });
    }, [floorplans]);

    // Filter floorplans
    const filteredFloorplans = useMemo(() => {
        return transformedFloorplans.filter(plan => {
            if (filters.homeType && plan.homeType !== filters.homeType) return false;

            if (filters.beds) {
                const minBeds = parseInt(filters.beds);
                const planBeds = parseInt(plan.beds);
                if (isNaN(planBeds) || planBeds < minBeds) return false;
            }

            return true;
        });
    }, [transformedFloorplans, filters]);

    return {
        availableHomeTypes,
        availableBeds,
        filteredFloorplans
    };
}
